// src/cockpit4/workspaces/MediaWorkbench.tsx
// ============================================
// 自媒体 AI 工作台（嵌入式）
//
// 背景：自媒体 AI 工作台是 D:/XEAOS/workbench-media 下的单文件页（15 个页面，
//       自带左侧 sidebar 导航 + hash 路由），构建后复制到 public/workbench/index.html。
//
// 两种用法：
//   1. 独立 tab（默认 / chrome 模式）：主站顶栏「自媒体」，顶部再加一条 15 页二级导航，
//      解决「顶栏塞不下 15 个页面」的问题。
//   2. 嵌入内容工厂（embedded 模式，v6.45）：只渲染 iframe —— 工作台自己的 sidebar
//      就是导航，再套一条顶栏等于两套导航重复。左栏给工作台，右栏留给每日计划。
//
// 跨栏联动：右侧每日计划点「去写稿」→ 父组件通过 goto 属性下发
//   { page:'create', plat:'抖音个人号' } → postMessage 给 iframe → 工作台切页并预选平台。
//   （用 postMessage 而不是改 iframe.src，避免整页重载丢掉台账/表单填写状态）
// ============================================

import { useEffect, useRef, useState } from "react";

interface NavItem {
  id: string;
  label: string;
  hint?: string;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

/** 与 workbench 的 NAV 一一对应；改那边记得同步这里 */
const NAV_GROUPS: NavGroup[] = [
  {
    group: "日常",
    items: [
      { id: "overview", label: "总览", hint: "指标 · 卡点 · 今日前 3 条" },
      { id: "hot", label: "热点追踪", hint: "分级 · 可一键转创作" },
      { id: "plan", label: "选题规划", hint: "默认只放 3 条" },
      { id: "create", label: "AI 创作", hint: "按平台模板逐字段出稿" },
    ],
  },
  {
    group: "抖音企业号",
    items: [
      { id: "dyhealth", label: "账号体检", hint: "数据 · 根因 · 打法" },
      { id: "dycoll", label: "我的合集", hint: "历史段 · 待建 · 后台真实列表" },
      { id: "dytopic", label: "创作方向", hint: "S01–S22 可点选改状态" },
      { id: "dylive", label: "直播方向", hint: "排期 · 复盘 · 切片" },
      { id: "fc", label: "流量预测", hint: "只给区间，不给点预测" },
    ],
  },
  {
    group: "私域（微信）",
    items: [{ id: "pv", label: "私域变现", hint: "8000 人 · 开课 · 招生 · 进群" }],
  },
  {
    group: "发布与复盘",
    items: [
      { id: "pubrec", label: "发布台账", hint: "记一条 · 行内可改" },
      { id: "review", label: "数据复盘", hint: "含成长曲线" },
    ],
  },
  {
    group: "沉淀",
    items: [
      { id: "idea", label: "灵感记录", hint: "可一键转选题" },
      { id: "rules", label: "规则中心", hint: "发布前 14 条自检" },
      { id: "profile", label: "个人经历库", hint: "可讲 / 禁讲" },
    ],
  },
];

const ALL = NAV_GROUPS.flatMap((g) => g.items);
const LS_KEY = "xeaos_mw_page_v1";
export const WB_BASE = "/workbench/index.html";
export const WB_VER = "20261102c"; // 改动 workbench 后改这个数字，避免浏览器用旧缓存

/** 父组件 → iframe 的切页指令（用于「每日计划 → 去写稿」这类跨栏联动） */
export interface WbGoto {
  page: string;
  /** 平台名用工作台模板里的中文名（如「抖音个人号」），不是主站的 key */
  plat?: string;
  /** 同一个 page 再点一次也要能触发，所以带个自增号 */
  nonce: number;
}

interface Props {
  /** 嵌入内容工厂左栏：不渲染 15 页二级导航（iframe 内自带 sidebar） */
  embedded?: boolean;
  /** 外部下发的切页指令 */
  goto?: WbGoto | null;
}

export default function MediaWorkbench({ embedded = false, goto }: Props) {
  const [page, setPage] = useState<string>(() => {
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem(LS_KEY) : null;
    return saved && ALL.some((x) => x.id === saved) ? saved : "overview";
  });
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLIFrameElement | null>(null);

  // 首次挂载：把地址栏里的 #page 也认一下（支持从别处深链进来）
  useEffect(() => {
    const h = (location.hash || "").replace("#", "");
    if (h && ALL.some((x) => x.id === h) && h !== page) setPage(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切页：优先改 iframe 的 hash（同源，不重载、不丢填写状态）
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, page); } catch { /* 隐私模式忽略 */ }
    const f = ref.current;
    if (!f || !loaded) return;
    try {
      const w = f.contentWindow as (Window & typeof globalThis) | null;
      if (w && w.location.hash !== "#" + page) w.location.hash = page;
    } catch {
      f.src = WB_BASE + "?v=" + WB_VER + (embedded ? "&embed=1" : "") + "#" + page;
    }
  }, [page, loaded]);

  // 外部指令：postMessage 给 iframe（工作台内部监听 wb:goto，切页 + 预选平台）
  useEffect(() => {
    if (!goto || !loaded) return;
    if (!ALL.some((x) => x.id === goto.page)) return;
    setPage(goto.page);
    try {
      const w = ref.current?.contentWindow;
      w?.postMessage({ type: "wb:goto", page: goto.page, plat: goto.plat }, "*");
    } catch { /* 跨源或被拦截时忽略，setPage 已保证至少切到该页 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goto?.nonce, loaded]);

  const cur = ALL.find((x) => x.id === page);

  // 嵌入内容工厂左栏时带 embed=1：工作台内部把 sidebar 收窄（236→196），给正文多留 40px
  const src = WB_BASE + "?v=" + WB_VER + (embedded ? "&embed=1" : "") + "#" + page;

  const iframe = (
    <iframe
      ref={ref}
      title="自媒体 AI 工作台"
      src={src}
      onLoad={() => setLoaded(true)}
      style={{ width: "100%", height: "100%", border: "none", display: "block" }}
    />
  );

  // ============ 嵌入模式：只给 iframe，导航交给工作台自带 sidebar ============
  if (embedded) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 0, background: "#0b1020" }}>
        {iframe}
        {!loaded && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              fontSize: 12.5,
              color: "rgba(232,238,252,.45)",
              pointerEvents: "none",
            }}
          >
            正在加载「{cur?.label || "自媒体工作台"}」…
          </div>
        )}
      </div>
    );
  }

  // ============ 独立 tab：带 15 页二级导航 ============
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, color: "#e8eefc" }}>
      {/* 二级导航：15 个页面按原分组排开 */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 14,
          padding: "10px 16px",
          borderBottom: "1px solid rgba(255,255,255,.08)",
          background: "rgba(12,18,32,.72)",
          backdropFilter: "blur(8px)",
          flex: "none",
        }}
      >
        {NAV_GROUPS.map((g) => (
          <div key={g.group} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontSize: 10.5,
                letterSpacing: 1,
                color: "rgba(232,238,252,.34)",
                marginRight: 2,
                whiteSpace: "nowrap",
              }}
            >
              {g.group}
            </span>
            {g.items.map((it) => {
              const on = it.id === page;
              return (
                <button
                  key={it.id}
                  title={it.hint}
                  onClick={() => setPage(it.id)}
                  style={{
                    padding: "5px 11px",
                    borderRadius: 7,
                    fontSize: 12.5,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    border: on ? "1px solid rgba(244,114,182,.55)" : "1px solid rgba(255,255,255,.08)",
                    background: on ? "rgba(244,114,182,.16)" : "transparent",
                    color: on ? "#F9A8D4" : "rgba(232,238,252,.66)",
                    fontWeight: on ? 700 : 500,
                    transition: "all .15s",
                  }}
                >
                  {it.label}
                </button>
              );
            })}
          </div>
        ))}

        <a
          href={WB_BASE + "?v=" + WB_VER + "#" + page}
          target="_blank"
          rel="noreferrer"
          style={{
            marginLeft: "auto",
            fontSize: 11.5,
            color: "rgba(232,238,252,.5)",
            textDecoration: "none",
            borderBottom: "1px dashed rgba(232,238,252,.25)",
            whiteSpace: "nowrap",
          }}
        >
          新窗口打开 ↗
        </a>
      </div>

      {/* 当前页说明 + iframe */}
      <div style={{ flex: 1, minHeight: 0, position: "relative", background: "#0b1020" }}>
        {iframe}
        {!loaded && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              fontSize: 12.5,
              color: "rgba(232,238,252,.45)",
              pointerEvents: "none",
            }}
          >
            正在加载「{cur?.label || "自媒体工作台"}」…
          </div>
        )}
      </div>
    </div>
  );
}
